'use client';

import { useMemo } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

/**
 * State for the action-center filters as backed by the URL.
 *
 * - `alertTypes`: multi-select array of alert type keys. Empty means "all alert types".
 * - `primaryAdvisorIds`: when `null`, the URL does not explicitly say anything about
 *   the primary advisor, so consumers should fall back to a default (the logged-in
 *   advisor). When set, an empty array means "all advisors" (explicit), and a
 *   non-empty array means only show alerts for those advisors.
 */
export type ActionCenterUrlState = {
  readonly alertTypes: readonly string[];
  readonly primaryAdvisorIds: readonly string[] | null;
};

type ActionCenterUrlStateAction =
  | { type: 'setAlertTypes'; alertTypes: readonly string[] }
  | { type: 'setPrimaryAdvisorIds'; primaryAdvisorIds: readonly string[] | null }
  | {
      type: 'setAllFilters';
      alertTypes: readonly string[];
      primaryAdvisorIds: readonly string[] | null;
    }
  | { type: 'clearAllFilters' };

const PRIMARY_ADVISOR_ALL_SENTINEL = 'all';

function parseCsv(value: string | null | undefined): readonly string[] {
  if (value === null || value === undefined || value === '') return [];
  return value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '');
}

export function useActionCenterUrlState(): ActionCenterUrlState {
  const searchParams = useSearchParams();

  return useMemo((): ActionCenterUrlState => {
    const alertTypesParam = searchParams?.get('alertType') ?? null;
    const primaryAdvisorParam = searchParams?.get('primaryAdvisor') ?? null;

    let primaryAdvisorIds: readonly string[] | null;
    if (primaryAdvisorParam === null) {
      primaryAdvisorIds = null;
    } else if (primaryAdvisorParam === PRIMARY_ADVISOR_ALL_SENTINEL || primaryAdvisorParam === '') {
      primaryAdvisorIds = [];
    } else {
      primaryAdvisorIds = parseCsv(primaryAdvisorParam);
    }

    return {
      alertTypes: parseCsv(alertTypesParam),
      primaryAdvisorIds,
    };
  }, [searchParams]);
}

export function useActionCenterUrlStateDispatch() {
  const router = useRouter();
  const pathname = usePathname();
  const state = useActionCenterUrlState();

  return (action: ActionCenterUrlStateAction) => {
    const newState = actionCenterUrlStateReducer(state, action);
    const searchParams = new URLSearchParams();

    if (newState.alertTypes.length > 0) {
      searchParams.set('alertType', newState.alertTypes.join(','));
    }
    if (newState.primaryAdvisorIds !== null) {
      if (newState.primaryAdvisorIds.length === 0) {
        searchParams.set('primaryAdvisor', PRIMARY_ADVISOR_ALL_SENTINEL);
      } else {
        searchParams.set('primaryAdvisor', newState.primaryAdvisorIds.join(','));
      }
    }

    const queryString = searchParams.toString();
    const targetPath = pathname ?? '';
    router.replace(queryString === '' ? targetPath : `${targetPath}?${queryString}`);
  };
}

function actionCenterUrlStateReducer(
  state: ActionCenterUrlState,
  action: ActionCenterUrlStateAction,
): ActionCenterUrlState {
  switch (action.type) {
    case 'setAlertTypes':
      return { ...state, alertTypes: action.alertTypes };
    case 'setPrimaryAdvisorIds':
      return { ...state, primaryAdvisorIds: action.primaryAdvisorIds };
    case 'setAllFilters':
      return { alertTypes: action.alertTypes, primaryAdvisorIds: action.primaryAdvisorIds };
    case 'clearAllFilters':
      // `null` (not `[]`) so the URL drops the `primaryAdvisor` param entirely
      // and the default-to-self resolver in `filter-defaults.ts` takes over.
      // Returning `[]` would persist the "show every advisor" sentinel and
      // permanently lock the user out of the default-to-self behavior.
      return { alertTypes: [], primaryAdvisorIds: null };
  }
  return state;
}
